import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Modal } from 'react-bootstrap';
import EffectBadge from 'components/EffectBadge';
import Image from 'components/Image';
import PillBadge from 'components/PillBadge';
import Video from 'components/Video';
import { selectProductPromotions } from 'stores/selectors/product-promo';
import codify from 'utils/codify';
import styles from './ProductHero.module.scss';
import ProductHeroAlternateImages from './ProductHeroAlternateImages';
import CloseIcon from '../../svg/CloseIcon';

function ProductHero({
  backInStockText,
  badgeImages,
  category,
  closeoutText,
  collection,
  defaultProductImageHeight,
  defaultProductImageWidth,
  defaultProductImageUrl,
  discountPercent,
  effect,
  featuredProductText,
  images: productImages,
  inventoryStatus,
  newProductText,
  priceDropText,
  productName,
  SKU,
  storeSelection,
  tags,
}) {
  const { productPromoBySku = {}, productPromoByCategory = {} } = useSelector(
    (state) => selectProductPromotions(state)
  );

  const promo =
    productPromoBySku[SKU] || productPromoByCategory[category?.toLowerCase()];
  const images = useMemo(
    () =>
      productImages?.length
        ? productImages
        : [
            {
              dimensions: {
                height: defaultProductImageHeight,
                width: defaultProductImageWidth,
              },
              url: defaultProductImageUrl,
            },
          ],
    [
      defaultProductImageHeight,
      defaultProductImageUrl,
      defaultProductImageWidth,
      productImages,
    ]
  );
  const mainImage = images[0];
  const imagesLength = images.filter(({ videoUrl }) => !videoUrl).length;
  const [passedImages, setPassedImages] = useState(images);
  const [largeImage, setLargeImage] = useState(mainImage);
  const [modalIsOpen, setModalIsOpen] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);

  const setLargeImageCallback = useCallback(({ dimensions, url }) => {
    setLargeImage({ dimensions, url });
  }, []);

  const playVideo = useCallback((src) => {
    setVideoSrc(src);
    setModalIsOpen(true);
  }, []);

  const hideVideo = useCallback(() => {
    setModalIsOpen(null);
    setVideoSrc(null);
  }, []);

  useEffect(() => {
    if (images !== passedImages) {
      setPassedImages(images);
      setLargeImage(images[0]);
    }
  }, [images, passedImages]);

  const codifiedValue = codify(collection || effect);
  const badgeImage = codifiedValue
    ? badgeImages.find((badge) => badge.title === `badge-${codifiedValue}.svg`)
    : null;

  const showDiscount = discountPercent > 0;

  const productStatus = tags?.includes('Closeout')
    ? 'CLOSEOUT'
    : tags?.includes('Featured Product')
    ? 'FEATURED'
    : inventoryStatus;

  return largeImage ? (
    <>
      <div
        id="product-hero-wrapper"
        className="d-flex flex-column flex-xl-row w-100"
      >
        <ProductHeroAlternateImages
          className="d-none d-xl-block"
          images={images}
          imagesLength={imagesLength}
          largeImage={largeImage}
          playVideo={playVideo}
          productName={productName}
          setLargeImage={setLargeImageCallback}
        />
        <div className="ms-lg-15 ms-xxl-20">
          <div className={styles.main_image_outer}>
            <div className={`position-relative mx-auto ${styles.main_image}`}>
              <div
                className={`d-flex position-absolute ${styles.main_image_inner}`}
              >
                <EffectBadge
                  badgeImageUrl={badgeImage?.url}
                  effect={collection || effect}
                  onPdp
                />
                <div
                  className={classNames(
                    'position-absolute',
                    styles.badge_container_top_right
                  )}
                >
                  {showDiscount && (
                    <PillBadge id="discount-badge" onPdp>
                      {`${discountPercent}% off`}
                    </PillBadge>
                  )}
                  {storeSelection && productStatus === 'NEW' && (
                    <PillBadge
                      customCss="bg-secondary text-primary text-uppercase"
                      id="new-product-badge"
                      onPdp
                    >
                      {newProductText}
                    </PillBadge>
                  )}
                  {storeSelection && productStatus === 'BACK IN STOCK' && (
                    <PillBadge
                      customCss="bg-secondary text-primary text-uppercase"
                      id="back-in-stock-badge"
                      onPdp
                    >
                      {backInStockText}
                    </PillBadge>
                  )}
                  {storeSelection && productStatus === 'CLOSEOUT' && (
                    <PillBadge
                      customCss="bg-red text-white text-uppercase"
                      id="closeout-badge"
                      onPdp
                    >
                      {closeoutText}
                    </PillBadge>
                  )}
                  {storeSelection && productStatus === 'FEATURED' && (
                    <PillBadge id="featured-product-badge" onPdp>
                      {featuredProductText}
                    </PillBadge>
                  )}
                  {promo && (
                    <PillBadge
                      customCss={`bg-primary text-white text-${
                        promo?.textTransform || 'uppercase'
                      }`}
                      id="promo-badge"
                      onPdp
                    >
                      {promo?.label}
                    </PillBadge>
                  )}
                  {storeSelection && tags?.includes('Price Drop') && (
                    <PillBadge
                      customCss="bg-primary text-white text-uppercase"
                      id="price-drop-badge"
                    >
                      {priceDropText}
                    </PillBadge>
                  )}
                </div>
                <div
                  className="w-100 h-auto position-relative"
                  data-value="pdp-main-image"
                >
                  <Image
                    fill
                    priority
                    alt={productName}
                    objectFit="contain"
                    sizes="(max-width: 991px) 100vw, 50vw"
                    src={largeImage.url}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ProductHeroAlternateImages
          className="d-block d-xl-none"
          images={images}
          imagesLength={imagesLength}
          largeImage={largeImage}
          playVideo={playVideo}
          productName={productName}
          setLargeImage={setLargeImageCallback}
        />
      </div>
      <Modal
        centered
        size="lg"
        className={styles.modal}
        show={modalIsOpen}
        onHide={hideVideo}
      >
        <Modal.Header className="d-flex bg-transparent justify-content-end align-items-end p-0">
          <button
            type="button"
            className="btn btn-transparent text-white p-2"
            onClick={hideVideo}
          >
            <CloseIcon />
          </button>
        </Modal.Header>
        <Modal.Body className="p-0 w-100 bg-transparent">
          <Video autoPlay controls desktopSrc={videoSrc} mobileSrc={videoSrc} />
        </Modal.Body>
      </Modal>
    </>
  ) : null;
}

export default memo(ProductHero);
